// src/pages/SubscribePage.jsx
// Subscribe page - 3-tier plan selection and Razorpay checkout

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, CreditCard, Loader2, Sparkles, Shield, ArrowRight } from 'lucide-react';
import { PUBLIC_PLANS, getPlanById, formatUsd } from '@/utils/payments/subscriptionPlans';
import { startSubscriptionCheckout } from '@/utils/payments/subscribe';
import { safeInternalPath } from '@/utils/payments/safeRedirect';
import { toast } from '@/hooks/use-toast';
import MetaTags from '@/components/SEO/MetaTags';
import BrandLogo from '@/components/brand/BrandLogo';

export default function SubscribePage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const redirectPath = safeInternalPath(searchParams.get('redirect'), '/app');

  // Support ?plan=monthly | yearly | lifetime
  const initialPlanId = searchParams.get('plan') || 'monthly';
  const [selectedPlanId, setSelectedPlanId] = useState(
    getPlanById(initialPlanId) ? initialPlanId : 'monthly'
  );

  const selectedPlan = getPlanById(selectedPlanId) || PUBLIC_PLANS[0];

  const handleSubscribe = async (planToBuy = selectedPlan) => {
    if (!planToBuy) return;

    // If unauthenticated, redirect to login preserving the plan and redirect intent
    if (!isAuthenticated) {
      const returnUrl = `/subscribe?plan=${encodeURIComponent(planToBuy.id)}&redirect=${encodeURIComponent(redirectPath)}`;
      navigate(`/authentication?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    if (paymentLoading) return;
    setPaymentLoading(true);

    try {
      await startSubscriptionCheckout({
        planType: planToBuy.planType || planToBuy.id,
        user,
        onSuccess: async ({ entitlement }) => {
          if (entitlement) {
            toast({
              variant: "success",
              title: "Payment successful",
              description: "Your subscription is now active. Redirecting...",
            });
          } else {
            toast({
              variant: "success",
              title: "Payment received",
              description: "Access activates as soon as the bank confirms. You can refresh in a moment.",
            });
          }
          setTimeout(() => {
            navigate(redirectPath);
          }, 1500);
        },
        onDismiss: () => {
          setPaymentLoading(false);
          toast({
            variant: "info",
            title: "Payment cancelled",
            description: "You can retry anytime.",
          });
        },
        onFail: (error) => {
          setPaymentLoading(false);
          if (error.message && (error.message.includes('cancelled') || error.message.includes('Payment cancelled'))) {
            toast({
              variant: "info",
              title: "Payment cancelled",
              description: "You can retry anytime.",
            });
          } else {
            toast({
              variant: "error",
              title: "Payment failed",
              description: error.message || "Failed to process payment. Please try again.",
            });
          }
        }
      });
    } catch (error) {
      setPaymentLoading(false);
      toast({
        variant: "error",
        title: "Payment failed",
        description: error.message || "Failed to initiate payment. Please try again.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-cream-paper py-8 sm:py-14 px-4 sm:px-6">
      <MetaTags
        title="ByOnco PRO Subscription & Plans"
        description="Choose a ByOnco plan: monthly, annual, or lifetime access to oncology navigation, hospital matching, cost clarity, and expert second opinions."
        path="/subscribe"
      />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12">
          <div className="flex justify-center mb-4">
            <BrandLogo size="md" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-forest-ink tracking-tight mb-3">
            Choose Your ByOnco PRO Plan
          </h1>
          <p className="text-charcoal/80 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Unlimited oncology companion access, explainable hospital matching, cost transparency, and expert-written second opinions.
          </p>
          <Link
            to="/lifetime-draw"
            className="inline-block mt-5 text-sm text-forest-ink underline underline-offset-4"
          >
            Payment does not create a lifetime-draw code. The draw is separate from checkout.
          </Link>
        </div>

        {/* Plan Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {PUBLIC_PLANS.map((plan) => {
            const isSelected = selectedPlanId === plan.id;
            const isYearly = plan.id === 'yearly';
            const isLifetime = plan.id === 'lifetime';

            return (
              <Card
                key={plan.id}
                onClick={() => setSelectedPlanId(plan.id)}
                className={`relative flex flex-col justify-between cursor-pointer transition-all duration-200 ${
                  isSelected
                    ? 'border-2 border-forest-ink shadow-lg bg-keylime-wash/30 scale-[1.02]'
                    : 'border border-border-mist bg-white hover:border-forest-ink/40'
                }`}
              >
                {/* Badges */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-forest-ink text-white text-xs font-medium px-3 py-1 rounded-full uppercase tracking-wider">
                    {plan.badge}
                  </div>
                )}
                {isYearly && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-700 text-white text-xs font-medium px-3 py-1 rounded-full uppercase tracking-wider">
                    Save 25%
                  </div>
                )}

                <CardHeader className="pt-7 pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl font-normal text-forest-ink">{plan.name}</CardTitle>
                      <p className="text-xs text-charcoal/70 mt-1">{plan.subtitle}</p>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        isSelected ? 'border-forest-ink bg-forest-ink' : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  </div>

                  <div className="mt-4 pt-2 border-t border-border-mist/60">
                    <div className="flex items-baseline gap-2">
                      {plan.compareAt && (
                        <s className="text-sm text-charcoal/50">{formatUsd(plan.compareAt)}</s>
                      )}
                      <span className="text-3xl font-bold text-forest-ink">
                        {formatUsd(plan.amount)}
                      </span>
                      <span className="text-sm text-charcoal/70">{plan.period}</span>
                    </div>
                    {isYearly && (
                      <p className="text-xs text-emerald-800 font-medium mt-1">
                        Equivalent to $14.99/month · Billed annually
                      </p>
                    )}
                    {isLifetime && (
                      <p className="text-xs text-charcoal/70 mt-1">
                        One single payment · No renewals ever
                      </p>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col justify-between pt-2">
                  <div className="space-y-2.5 mb-6">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm">
                        <CheckCircle2 className="w-4 h-4 text-forest-ink mt-0.5 flex-shrink-0" />
                        <span className="text-charcoal/90">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPlanId(plan.id);
                      handleSubscribe(plan);
                    }}
                    disabled={paymentLoading}
                    variant={isSelected ? 'default' : 'outline'}
                    className={`w-full py-5 text-sm font-medium ${
                      isSelected
                        ? 'bg-forest-ink hover:bg-forest-shadow text-white'
                        : 'border-forest-ink text-forest-ink hover:bg-forest-ink hover:text-white'
                    }`}
                  >
                    {paymentLoading && selectedPlanId === plan.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        {isAuthenticated ? `Subscribe with ${plan.name}` : `Select ${plan.name}`}
                        <ArrowRight className="ml-1.5 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Trust & Guarantee Strip */}
        <div className="bg-keylime-wash border border-border-mist rounded-xl p-5 sm:p-6 text-center max-w-2xl mx-auto space-y-2">
          <div className="flex items-center justify-center gap-2 text-forest-ink font-medium text-sm">
            <Shield className="w-4 h-4" />
            <span>Bank-grade security powered by Razorpay</span>
          </div>
          <p className="text-xs text-charcoal/70 leading-relaxed">
            Subscriptions can be managed or cancelled anytime. Instant activation upon bank confirmation. Need institutional invoicing? Reach out at contact@praesidiocare.com.
          </p>
        </div>
      </div>
    </div>
  );
}
